import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import DiscountIcon from "@mui/icons-material/Discount";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCourseTypes } from "../../State/Enum/Action";
import { getBasicCourseDetail, updateCourse } from "../../State/Course/Action";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UpdateCourse = () => {
  const { courseId } = useParams();
  const { enums, course } = useSelector((store) => store);
  const [courseType, setCourseType] = useState("");
  const [file, setFile] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const basicCourseDetail = useSelector(
    (store) => store.course.basicCourseDetail
  );

  useEffect(() => {
    dispatch(getCourseTypes());
    if (courseId) {
      dispatch(getBasicCourseDetail(courseId));
    }
  }, [dispatch, courseId]);

  useEffect(() => {
    if (basicCourseDetail) {
      setCourseName(basicCourseDetail.name || "");
      setCourseDescription(basicCourseDetail.description || "");
      setCourseType(basicCourseDetail.type || "");
      setPrice(basicCourseDetail.price?.toString() || "");
      setDiscount(
        basicCourseDetail.discountAmount?.toString() ||
          basicCourseDetail.discount?.toString() ||
          "0"
      );
    }
  }, [basicCourseDetail]);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [file]);

  const handleChange = (event) => {
    setCourseType(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const courseData = new FormData();
      courseData.append("name", courseName);
      courseData.append("description", courseDescription);
      courseData.append("type", courseType);
      courseData.append("price", price);
      courseData.append("discountAmount", discount || "0");

      // Only append thumbnail if a new file is selected
      if (file) {
        courseData.append("thumbnail", file);
      }

      await dispatch(updateCourse(courseId, courseData));
      navigate("/manage-my-courses");
    } catch (error) {
      console.error("Error updating course:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const finalPrice = Math.max(
    0,
    (Number(price) || 0) - (Number(discount) || 0)
  );

  const isCourseLoading = useSelector((store) => store.course.isLoading);

  if (isCourseLoading && !basicCourseDetail) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(to right, #667eea, #764ba2)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const thumbnailPreview = previewUrl || basicCourseDetail?.thumbnailUrl;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: { xs: 3, md: 5 },
        px: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 800,
          mx: "auto",
        }}
      >
        {/* Header with back button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
          }}
        >
          <IconButton
            onClick={() => navigate("/manage-my-courses")}
            sx={{
              mr: 2,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: "white",
              display: "flex",
              alignItems: "center",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            <SchoolIcon sx={{ mr: 1 }} />
            Update Course
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          {/* Course Thumbnail Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: { xs: 3, md: 4 },
              px: { xs: 2, md: 3 },
            }}
          >
            {thumbnailPreview && (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: "relative",
                  maxWidth: { xs: "100%", sm: 400, md: 450 },
                  width: "100%",
                  aspectRatio: "16/9",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.3)",
                    borderColor: "#667eea",
                    "& .change-image-overlay": {
                      opacity: 1,
                    },
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Box
                  component="img"
                  src={thumbnailPreview}
                  alt="Course Thumbnail"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                {/* Hover Overlay with Change Image Icon */}
                <Box
                  className="change-image-overlay"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(102, 126, 234, 0.85)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    backdropFilter: "blur(3px)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      color: "white",
                    }}
                  >
                    <IconButton
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.25)",
                        color: "white",
                        width: 64,
                        height: 64,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.35)",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                        textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                        fontSize: "1.1rem",
                      }}
                    >
                      Change Image
                    </Typography>
                  </Box>
                </Box>
                {file && (
                  <Chip
                    label="New Image Selected"
                    color="success"
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: "rgba(76, 175, 80, 0.95)",
                      color: "white",
                      fontWeight: "bold",
                      zIndex: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  />
                )}
              </Box>
            )}
            {!thumbnailPreview && (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  maxWidth: { xs: "100%", sm: 400, md: 450 },
                  width: "100%",
                  aspectRatio: "16/9",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 3,
                  border: "2px dashed #d0d0d0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  gap: 2,
                  "&:hover": {
                    borderColor: "#667eea",
                    backgroundColor: "#f0f4ff",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ImageIcon sx={{ fontSize: 48, color: "#999" }} />
                <Typography variant="body1" color="text.secondary">
                  Click to upload thumbnail
                </Typography>
              </Box>
            )}
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <form onSubmit={handleSubmit}>
            {/* Hidden file input for image upload */}
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={(e) => setFile(e.target.files[0])}
            />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  variant="outlined"
                  label="Course Name"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  helperText={`${courseName.length}/1000 characters`}
                  inputProps={{
                    maxLength: 1000,
                  }}
                  required
                  InputProps={{
                    startAdornment: <SchoolIcon sx={{ mr: 1, color: "#667eea" }} />,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                    "& .MuiFormHelperText-root": {
                      color:
                        courseName.length > 800
                          ? "#f59e0b"
                          : courseName.length > 900
                          ? "#ef4444"
                          : "#64748b",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  variant="outlined"
                  label="Description"
                  multiline
                  rows={4}
                  value={courseDescription}
                  onChange={(e) => setCourseDescription(e.target.value)}
                  helperText={`${courseDescription.length}/1000 characters`}
                  inputProps={{
                    maxLength: 1000,
                  }}
                  required
                  InputProps={{
                    startAdornment: (
                      <DescriptionIcon sx={{ mr: 1, color: "#667eea" }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                    "& .MuiFormHelperText-root": {
                      color:
                        courseDescription.length > 800
                          ? "#f59e0b"
                          : courseDescription.length > 900
                          ? "#ef4444"
                          : "#64748b",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel
                    sx={{
                      "&.Mui-focused": {
                        color: "#667eea",
                      },
                    }}
                  >
                    <CategoryIcon sx={{ mr: 1 }} />
                    Course Type
                  </InputLabel>
                  <Select
                    value={courseType}
                    onChange={handleChange}
                    name="type"
                    label="Course Type"
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea",
                      },
                    }}
                  >
                    {enums.courseTypes?.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  name="price"
                  fullWidth
                  variant="outlined"
                  label="Course Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <CurrencyRupeeIcon sx={{ mr: 1, color: "#667eea" }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  name="discountAmount"
                  fullWidth
                  variant="outlined"
                  label="Discount Amount"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <DiscountIcon sx={{ mr: 1, color: "#667eea" }} />
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#f0f4ff",
                    borderRadius: 2,
                    border: "2px solid #667eea",
                  }}
                >
                  <TextField
                    name="finalPrice"
                    fullWidth
                    variant="outlined"
                    label="Final Price"
                    value={`₹${finalPrice.toLocaleString("en-IN")}`}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <CurrencyRupeeIcon sx={{ mr: 1, color: "#667eea" }} />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                      },
                      "& .MuiInputLabel-root": {
                        color: "#667eea",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={isLoading}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    py: 1.5,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      opacity: 0.7,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isLoading ? "Updating Course..." : "Save Changes"}
                </Button>
              </Grid>

              {course?.error && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: "#ffebee",
                      border: "1px solid #f44336",
                      borderRadius: 2,
                    }}
                  >
                    <Typography color="error" sx={{ fontWeight: "medium" }}>
                      {course.error?.message || course.error || "An error occurred"}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
    </Box>
  );
};

export default UpdateCourse;

